import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/primitives";
import type { AccountUser, Accounts } from "@/lib/accounts";

type Mode = "signin" | "signup" | "forgot";

/** Email/password + "Continue with Google" form for a Supabase-backed
 * `Accounts` (R4/R6). Handles the full email flow: sign in, sign up (with a
 * "check your email" pending state + resend), and forgot-password. Shared by the
 * top-bar sign-in chip and the full-page signed-out prompt. */
export function SupabaseAuthForm({
  accs,
  onSignedIn,
  compact = false,
}: {
  accs: Accounts;
  onSignedIn: (user: AccountUser) => void;
  compact?: boolean;
}) {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Set to the address awaiting verification after a confirmation-required
  // sign-up — swaps the form for a "check your email" panel.
  const [pending, setPending] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !email) return;

    if (mode === "forgot") {
      if (!accs.resetPassword) return;
      setBusy(true);
      void accs
        .resetPassword(email)
        .then(() => {
          toast.success("Check your email for a password-reset link");
          setMode("signin");
        })
        .catch((err: unknown) => toast.error("Couldn't send reset email", { description: String(err) }))
        .finally(() => setBusy(false));
      return;
    }

    if (!password) return;
    setBusy(true);
    if (mode === "signin") {
      void accs
        .signInWithPassword!(email, password)
        .then((u) => {
          toast.success(`Signed in as ${u.email ?? u.uid}`);
          onSignedIn(u);
        })
        .catch((err: unknown) => toast.error("Sign-in failed", { description: String(err) }))
        .finally(() => setBusy(false));
      return;
    }

    // sign up
    void accs
      .signUp!(email, password)
      .then((res) => {
        if (res.needsVerification) {
          setPending(email); // NOT signed in yet — they must confirm first
        } else {
          toast.success(`Account created — signed in as ${res.user.email ?? res.user.uid}`);
          onSignedIn(res.user);
        }
      })
      .catch((err: unknown) => toast.error("Sign-up failed", { description: String(err) }))
      .finally(() => setBusy(false));
  };

  const google = () => {
    setBusy(true);
    void accs.signInWithGoogle().catch((err: unknown) => {
      toast.error("Sign-in failed", { description: String(err) });
      setBusy(false);
    });
    // No re-enable on success: signInWithOAuth navigates the whole page away.
  };

  const width = compact ? "w-56" : "w-64";

  if (pending) {
    return (
      <div className={`flex ${width} flex-col gap-2 text-sm`}>
        <p className="text-muted-foreground">
          Check <span className="font-medium">{pending}</span> for a verification link. Open it to
          finish signing in.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => {
            if (!accs.resendVerification) return;
            setBusy(true);
            void accs
              .resendVerification(pending)
              .then(() => toast.success("Verification email re-sent"))
              .catch((err: unknown) => toast.error("Couldn't resend", { description: String(err) }))
              .finally(() => setBusy(false));
          }}
        >
          Resend email
        </Button>
        <button
          type="button"
          className="text-left text-2xs text-muted-foreground underline underline-offset-2 hover:text-muted-foreground"
          onClick={() => {
            setPending(null);
            setMode("signin");
          }}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`flex ${width} flex-col gap-2`}>
      <Input
        type="email"
        placeholder="you@example.com"
        value={email}
        autoComplete="email"
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      {mode !== "forgot" && (
        <Input
          type="password"
          placeholder="Password"
          value={password}
          autoComplete={mode === "signin" ? "current-password" : "new-password"}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />
      )}
      <Button type="submit" size="sm" disabled={busy}>
        {mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
      </Button>

      {mode === "signin" && accs.resetPassword && (
        <button
          type="button"
          className="text-left text-2xs text-muted-foreground underline underline-offset-2 hover:text-muted-foreground"
          onClick={() => setMode("forgot")}
        >
          Forgot password?
        </button>
      )}
      <button
        type="button"
        className="text-left text-2xs text-muted-foreground underline underline-offset-2 hover:text-muted-foreground"
        onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
      >
        {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
      </button>

      {mode !== "forgot" && (
        <>
          <div className="my-1 h-px bg-border" />
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={google}>
            Continue with Google
          </Button>
        </>
      )}
    </form>
  );
}
